import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {ReactNode, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import SnackbarMessages from '../../common/SnackbarMessages';

export type BulkUpdateField = 'cam_monitor_battery' | 'has_motion_sensor';

const ENDPOINT_BY_FIELD: Record<BulkUpdateField, string> = {
  cam_monitor_battery: '/commtrac-node/miner/battery_monitoring',
  has_motion_sensor: '/commtrac-node/miner/motion_sensor',
};

interface Props extends Omit<DialogProps, 'onClose'> {
  ids: number[];
  field: BulkUpdateField;
  value: 0 | 1;
  title: string;
  summary: ReactNode;
  confirmLabel: string;
  successMessage: string;
  onSubmitted?: () => void;
  onClose?: () => void;
}

const CommtracNodeItemsBulkConfirmModal = ({
  ids,
  field,
  value,
  title,
  summary,
  confirmLabel,
  successMessage,
  onSubmitted,
  onClose,
  ...props
}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async () => {
    setIsSubmitting(true);
    try {
      await API.patch(`${apiBaseUrl}${ENDPOINT_BY_FIELD[field]}`, {
        ids,
        [field]: value,
      });
      enqueueSnackbar(successMessage, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onSubmitted?.();
      onClose?.();
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_bulkupd_01');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog {...props} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{summary}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()}>Cancel</Button>
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          autoFocus
          onClick={submit}
        >
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CommtracNodeItemsBulkConfirmModal;
